import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, Search, MoreVertical, Building2, TrendingUp, ChevronLeft, ChevronRight, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);

const maskCNPJ = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
};

const maskTelefone = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 11);
  if (d.length === 0)  return '';
  if (d.length <= 2)   return `(${d}`;
  if (d.length <= 6)   return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10)  return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
};

const Representadas = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [representadas, setRepresentadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => { fetchRepresentadas(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const fetchRepresentadas = async () => {
    try {
      const r = await api.get('/representadas');
      setRepresentadas(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Erro ao carregar representadas'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (window.confirm('Excluir esta representada? O registro será inativado.')) {
      try {
        await api.delete(`/representadas/${id}`);
        toast.success('Representada removida!');
        fetchRepresentadas();
      } catch { toast.error('Erro ao deletar'); }
    }
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const r = await api.post('/export/representadas-pdf', { ids: filtered.map(rep => rep.id) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `representadas_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const filtered = representadas.filter(r => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (r.nome || '').toLowerCase().includes(q) ||
      (r.nome_fantasia || '').toLowerCase().includes(q) ||
      (r.cnpj || '').toLowerCase().includes(q) ||
      (r.contato || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pagina = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const comissaoMedia = representadas.length
    ? representadas.reduce((a, r) => a + (r.comissao_padrao || 0), 0) / representadas.length
    : 0;

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Representadas...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Representadas</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Indústrias Representadas · {filtered.length} registros</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input
              placeholder="Buscar por nome, CNPJ..."
              className="text-xs font-semibold bg-transparent outline-none w-44 text-slate-700"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={handleExportPDF}
            disabled={pdfLoading}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <FileDown size={14}/> {pdfLoading ? 'Gerando...' : 'PDF'}
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/representadas/novo')}
              className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <Plus size={14}/> Nova Representada
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 mb-5">
        {[
          { label: 'Total de Representadas', value: representadas.length, icon: Building2, cor: '#1E40AF', fundo: '#EFF6FF' },
          { label: 'Comissão Média', value: `${fmt(comissaoMedia)}%`, icon: TrendingUp, cor: '#166534', fundo: '#DCFCE7' },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: k.fundo }}>
                <k.icon size={13} style={{ color: k.cor }}/>
              </div>
            </div>
            <p className="text-lg font-black" style={{ color: k.cor }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                <th className="px-4 py-3">Nome Fantasia</th>
                <th className="px-4 py-3">CNPJ</th>
                <th className="px-4 py-3">Contato</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3 text-right">Comissão Padrão</th>
                {isAdmin && <th className="px-4 py-3 text-center">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5}>
                    <EmptyState
                      icon={
                        <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                          <path d="M8 64h56" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M14 64V30l22-18 22 18v34" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="24" y="44" width="9" height="20" rx="1" stroke="#7BB8F0" strokeWidth="1.5"/>
                          <rect x="39" y="44" width="9" height="20" rx="1" stroke="#7BB8F0" strokeWidth="1.5"/>
                          <rect x="20" y="34" width="10" height="8" rx="1" stroke="#4A90D9" strokeWidth="1.5"/>
                          <rect x="42" y="34" width="10" height="8" rx="1" stroke="#4A90D9" strokeWidth="1.5"/>
                        </svg>
                      }
                      title="Nenhuma representada cadastrada"
                      subtitle="Cadastre as fábricas que você representa"
                      actionLabel={isAdmin ? '+ Nova Representada' : undefined}
                      onAction={isAdmin ? () => navigate('/representadas/novo') : undefined}
                    />
                  </td>
                </tr>
              ) : pagina.map((r, idx) => (
                <tr
                  key={r.id}
                  onClick={() => isAdmin && navigate(`/representadas/editar/${r.id}`)}
                  className={`text-xs border-b border-slate-100 transition-colors
                    ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}
                    ${isAdmin ? 'hover:bg-blue-50 cursor-pointer' : ''}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-black text-slate-800">{r.nome_fantasia || r.nome}</p>
                    {r.nome_fantasia && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{r.nome}</p>}
                  </td>
                  <td className="px-4 py-2.5 font-mono font-bold text-slate-600 whitespace-nowrap">{r.cnpj ? maskCNPJ(r.cnpj) : '—'}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-700">{r.contato || '—'}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-600 whitespace-nowrap">{r.telefone ? maskTelefone(r.telefone) : '—'}</td>
                  <td className="px-4 py-2.5 text-right font-black text-emerald-700 whitespace-nowrap">
                    {fmt(r.comissao_padrao)}%
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                            <MoreVertical size={14} className="text-slate-400"/>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl border-slate-200 shadow-xl bg-white">
                          <DropdownMenuItem
                            onClick={() => navigate(`/representadas/editar/${r.id}`)}
                            className="text-xs font-bold gap-2 cursor-pointer p-3 rounded-lg">
                            <Edit size={13} className="text-[#0A3D73]"/> Alterar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(r.id)}
                            className="text-xs font-bold gap-2 cursor-pointer text-red-600 p-3 rounded-lg">
                            <Trash2 size={13}/> Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer">
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14}/>
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {filtered.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Representadas;
